// containerFacts — a container material's preset kind and conformance as the
// client reads them (`Resource.Container`, server-projected on each library
// row as `container`). Pure: types and the wire parse.
//
// Parse, don't validate: an unknown kind is no container fact at all (the row
// falls back to "includes parts = a module"); an unknown reason is kept as
// `unknown`, so a newer server's reason still marks the container not
// conforming instead of vanishing.

export type {
    ContainerConformance,
    ContainerFacts,
    ContainerKind,
    NonConformance,
};
export { parseContainerFacts };

import { fields, list, str } from "./wireShape.js";

type ContainerKind = "module" | "course";

type NonConformance =
    | { readonly type: "container_empty" }
    | { readonly type: "part_is_course"; readonly part: string }
    | { readonly type: "part_not_page_or_module"; readonly part: string }
    | { readonly type: "part_is_container"; readonly part: string }
    | { readonly type: "part_removed"; readonly part: string }
    | { readonly type: "unknown" };

type ContainerConformance =
    | { readonly type: "conforming" }
    | {
          readonly type: "not_conforming";
          readonly reasons: readonly NonConformance[];
      };

type ContainerFacts = {
    readonly kind: ContainerKind;
    readonly conformance: ContainerConformance;
};

const kindOf = (raw: string | undefined): ContainerKind | undefined => {
    switch (raw) {
        case "module":
        case "course":
            return raw;
        default:
            return undefined;
    }
};

const reasonOf = (raw: unknown): NonConformance => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const part = str(f?.get("part"));
    switch (type) {
        case "container_empty":
            return { type };
        case "part_is_course":
        case "part_not_page_or_module":
        case "part_is_container":
        case "part_removed":
            return part === undefined ? { type: "unknown" } : { type, part };
        default:
            return { type: "unknown" };
    }
};

const conformanceOf = (raw: unknown): ContainerConformance => {
    const f = fields(raw);
    return str(f?.get("type")) === "not_conforming"
        ? {
              type: "not_conforming",
              reasons: list(f?.get("reasons")).map(reasonOf),
          }
        : { type: "conforming" };
};

const parseContainerFacts = (
    raw: unknown,
): { readonly container?: ContainerFacts } => {
    const f = fields(raw);
    const kind = kindOf(str(f?.get("kind")));
    return kind === undefined
        ? {}
        : {
              container: {
                  kind,
                  conformance: conformanceOf(f?.get("conformance")),
              },
          };
};
